<script>
import appConfig from '@/app.config'
import PageHeader from '@/components/page-header'
import PinCommand from './components/pinCommand'
import DeviceCommand from './components/deviceCommand'
import CustomProfileCommand from './components/customProfileCommand'
import LoadingSkeleton from './components/loadingSkeleton'
import Layout from '@/router/layouts/main'
import { Helper } from '@/mixin/helper'
import { GET_DEVICE_CODE } from '@/services/api/master/tracking'
import { GET_COMPANYS } from '@/services/api/master/companies'
import { getSmsClient } from '@/services/sms'
import { getToolsClient } from '@/services/tools'

const smsAxios = getSmsClient()
const toolsAxios = getToolsClient()

/**
 * Starter component
 */
export default {
  page: {
    title: 'Pengaturan Online Command',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    PinCommand,
    DeviceCommand,
    CustomProfileCommand,
    LoadingSkeleton,
  },
  mixins: [Helper],
  apollo: {
    companies: {
      query: GET_COMPANYS,
    },
    deviceTypes: {
      query: GET_DEVICE_CODE,
    },
  },
  data() {
    return {
      sensors: [
        {
          sensor_code: 0,
          sensor_name: 'Default Setting',
        },
      ],
      pinCodes: [],
      pinCommands: [],
      deviceCommands: [],
      deviceTypes: {
        deviceType: [],
      },
      companies: {
        companies: [],
      },
      isLoading: true,
      isTabDeviceCommand: false,
      isCustomProfileCommand: false,
    }
  },
  computed: {
    title() {
      return this.$t('onlineCommand.text')
    },
    sensorsForPinCommand() {
      return this.sensors.filter(sensor => sensor.sensor_code !== 0);
    }
  },
  watch: {
    isTabDeviceCommand(newVal) {
      if (!newVal || this.deviceCommands.length > 0) return
      this.getDeviceCommands()
    },
    isCustomProfileCommand(newVal) {
      if (!newVal || this.deviceCommands.length > 0) return
      this.getDeviceCommands()
    },
  },
  async created() {
    await this.$nextTick()
    await this.$apollo.queries.deviceTypes.refetch()
    await this.getSensors()
    await this.getPinCodes()
    await this.getPinCommands()
  },
  methods: {
    async getPinCommands(deviceCode) {
      try {
        this.isLoading = true
        let params = deviceCode
          ? {
              filters: `deviceCode:eq:${deviceCode}`,
            }
          : ''
        smsAxios
          .get('/pin-command', {
            params,
          })
          .then(async (res) => {
            res.data?.forEach(async (data) => {
              const device_name = await this.mapDeviceName(data.device_code)
              data['device_name'] = device_name
              const sensor_name = await this.mapSensorName(data.sensor_code)
              data['sensor_name'] = sensor_name
              const pin_name = await this.mapPinName(data.pin_code)
              data['pin_name'] = pin_name
            })
            await this.$nextTick()
            this.pinCommands = res.data ?? []
            this.isLoading = false
          })
      } catch (error) {
        this.isLoading = false
        this.handleError(error)
      }
    },
    async getDeviceCommands(deviceCode) {
      try {
        this.isLoading = true
        let params = deviceCode
          ? {
              filters: `deviceCode:eq:${deviceCode}`,
            }
          : ''
        smsAxios
          .get('/device-command', {
            params,
          })
          .then(async (res) => {
            res.data?.forEach(async (_res) => {
              const device_name = await this.mapDeviceName(_res.device_code)
              _res['device_name'] = device_name
              const sensor_name = await this.mapSensorName(_res.sensor_code)
              _res['sensor_name'] = sensor_name ?? '-'
              _res['expected_response'] = _res.expected_response ?? {}
            })
            await this.$nextTick()
            this.deviceCommands = res.data ?? []
            this.isLoading = false
          })
      } catch (error) {
        this.handleError(error)
        this.isLoading = false
      }
    },
    async getPinCodes() {
      try {
        this.isLoading = true
        await toolsAxios.get('pin-type').then((res) => {
          this.pinCodes = res.data
        })
        this.isLoading = false
      } catch (error) {
        this.handleError(error)
      }
    },
    async getSensors() {
      try {
        this.isLoading = true
        await toolsAxios.get('sensor-type').then((res) => {
          this.sensors.push(...res.data)
        })
        this.isLoading = false
      } catch (error) {
        this.handleError(error)
      }
    },
    mapDeviceName(device_code) {
      for (const device of this.deviceTypes.deviceType) {
        if (device.deviceCode === device_code) return device.deviceName
      }
    },
    mapPinName(pin_code) {
      return this.pinCodes?.find((e) => e.pin_code === pin_code)?.pin_name
    },

    mapSensorName(sensor_code) {
      if (!this.sensors) return
      return this.sensors.find((e) => e.sensor_code === sensor_code)?.sensor_name
    },
    updateChangeOnPin(device) {
      this.getPinCommands(device)
    },
    updateChangeOnDevice(device) {
      this.getDeviceCommands(device)
    },
    handleError(error) {
      if (error.response) {
        const errorStatus = error.response.status
        const errorData = error.response.data

        if (errorStatus === 400 && errorData.details) {
          errorData.details.forEach((element) => {
            this.$toast.error(`${errorData.error}: ${element.field} ${element.message}`)
          })
        } else if (errorStatus === 401) {
          this.$toast.error(errorData.error)
          this.$router.push({ name: 'Login' })
        } else if (errorStatus === 403) {
          this.$toast.error(errorData.error)
        } else {
          this.$toast.error(errorData.error)
        }
      } else if (error.request) {
        this.$toast.error(error.response.statusText)
      } else {
        this.$toast.error(error.message)
      }
    },
  },
}
</script>

<template>
  <Layout>
    <PageHeader :title="title" />
    <div class="card mc-card mc-card-front">
      <div class="card-body">
        <LoadingSkeleton v-if="isLoading" />
        <div v-show="!isLoading">
          <b-tabs content-class="p-3 text-muted">
            <b-tab active class="border-0">
              <template v-slot:title>
                <span class="d-none d-sm-inline-block">{{ $t('onlineCommand.pinCommand') }}</span>
              </template>
              <PinCommand
                :deviceTypes="deviceTypes.deviceType"
                :pinCodes="pinCodes"
                :sensors="sensorsForPinCommand"
                :pinCommands="pinCommands"
                @update-change="updateChangeOnPin"
                :isLoading="isLoading"
              />
            </b-tab>
            <b-tab @click="() => (isTabDeviceCommand = true)">
              <template v-slot:title>
                <span class="d-none d-sm-inline-block">{{ $t('onlineCommand.deviceCommand') }}</span>
              </template>
              <DeviceCommand
                :deviceTypes="deviceTypes.deviceType"
                :pinCodes="pinCodes"
                :sensors="sensors"
                :deviceCommands="deviceCommands"
                @update-change="updateChangeOnDevice"
              />
            </b-tab>
            <b-tab @click="() => (isCustomProfileCommand = true)">
              <template v-slot:title>
                <span class="d-none d-sm-inline-block">{{ $t('onlineCommand.customProfileCommand') }}</span>
              </template>
              <CustomProfileCommand
                v-if="this.deviceCommands.length > 0 && companies.companies?.length > 0"
                :deviceTypes="deviceTypes.deviceType"
                :sensors="sensors"
                :companies="companies.companies"
                :deviceCommands="deviceCommands"
              />
            </b-tab>
          </b-tabs>
        </div>
      </div>
    </div>
  </Layout>
</template>
